import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArrowRight, Globe, Layers, ShieldCheck } from "lucide-react";

// JSON-LD: edit social URLs here to expand Knowledge-Graph signals.
const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "AIGL Channel",
  alternateName: "AIGL Career Launch",
  url: "https://aiglchannel.info",
  logo: "https://aiglchannel.info/icons/icon-512.png",
  description:
    "AIGL Channel is a global entrepreneurial enablement firm. We productize expert-grade business knowledge into investor-ready blueprints, empowering founders in 38+ countries to launch institutional-grade businesses in days, not months.",
  foundingDate: "2024",
  slogan: "Business-in-a-Box. Architects for the new economy.",
  email: "support@aiglchannel.info",
  areaServed: "Worldwide",
  knowsAbout: [
    "Business Planning",
    "Cyber Security Training",
    "Security Guard Services",
    "VIP Personal Security",
    "Home Care Services",
    "Recruitment Agency",
    "Entrepreneurship",
    "Investor-ready Business Plans",
  ],
  sameAs: [
    // Replace with real handles when ready
    // "https://www.facebook.com/aiglchannel",
    // "https://www.instagram.com/aiglchannel",
    // "https://www.linkedin.com/company/aigl-channel",
    // "https://www.youtube.com/@aiglchannel",
  ],
};

export default function AboutPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "About Us · AIGL Channel — Architects for the new economy";

    // Append JSON-LD to <head> (search-engine friendly, idempotent)
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.aiglJsonld = "organization";
    script.text = JSON.stringify(ORGANIZATION_JSONLD);
    // Remove any prior copy to keep it idempotent across renders
    document.head
      .querySelectorAll('script[data-aigl-jsonld="organization"]')
      .forEach((s) => s.remove());
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      script.remove();
    };
  }, []);

  return (
    <div data-testid="about-page" className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-20 sm:pb-28">
          <div className="font-accent text-[11px] tracking-[0.3em] text-[#C5A059] mb-6">
            ABOUT US
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            We build the skeletons<br />
            <span className="text-[#C5A059]">founders use to build empires.</span>
          </h1>
          <p className="mt-8 text-white/70 text-base sm:text-lg max-w-2xl leading-relaxed">
            A global entrepreneurial enablement firm bridging the gap between
            <span className="text-white"> "having a vision" </span>
            and
            <span className="text-white"> "securing funding."</span>
          </p>
        </div>
      </section>

      {/* Mission block */}
      <section className="border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-24 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <div className="font-accent text-[10px] tracking-[0.3em] text-[#C5A059]">
              01 · WHO WE ARE
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#0A192F] leading-tight">
              About AIGL Channel
            </h2>
          </div>
          <div className="lg:col-span-8">
            <p className="text-slate-600 text-lg leading-relaxed">
              AIGL Channel is a global entrepreneurial enablement firm. We focus on bridging the gap between
              <span className="text-[#0A192F] font-semibold"> "having a vision" </span>
              and
              <span className="text-[#0A192F] font-semibold"> "securing funding."</span>
            </p>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              By packaging years of industry experience into structured, professional frameworks,
              we empower founders in
              <span className="text-[#0A192F] font-semibold"> 38+ countries </span>
              to launch institutional-grade businesses in days, not months.
            </p>

            <div className="mt-10 grid sm:grid-cols-3 gap-5">
              <StatCard label="COUNTRIES SERVED" value="38+" />
              <StatCard label="INSTANT DELIVERY" value="< 60s" />
              <StatCard label="FOUNDER FEE" value="One-time" />
            </div>
          </div>
        </div>
      </section>

      {/* Business model block */}
      <section className="bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-24 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <div className="font-accent text-[10px] tracking-[0.3em] text-[#C5A059]">
              02 · OUR MODEL
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#0A192F] leading-tight">
              Our Business Model: <span className="text-[#C5A059]">&apos;Business-in-a-Box&apos;</span>
            </h2>
            <p className="mt-4 text-slate-500 text-sm leading-relaxed">
              A direct-to-founder digital model — no calls, no consultants, no waiting.
            </p>
          </div>
          <div className="lg:col-span-8">
            <p className="text-slate-600 text-lg leading-relaxed">
              AIGL Career Launch operates on a direct-to-founder digital model. We productize
              high-stakes, expert-grade business knowledge into
              <span className="text-[#0A192F] font-semibold"> investor-ready blueprints.</span>
            </p>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              Instead of spending thousands on consultants or months on planning, entrepreneurs
              pay a one-time fee to get an instant foundation —
              <span className="text-[#0A192F] font-semibold"> P&amp;Ls, curricula, SWOTs</span>
              {" "}— for high-growth sectors.
            </p>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              We are the architects for the new economy, providing the skeletons that founders
              use to build their empires.
            </p>

            <div className="mt-10 grid sm:grid-cols-3 gap-5">
              <PillarCard
                icon={Globe}
                title="Global Reach"
                desc="Frameworks usable in 38+ countries — currency-aware, sector-tested."
              />
              <PillarCard
                icon={Layers}
                title="Institutional Grade"
                desc="P&Ls, curricula, SWOTs and SOPs the same shape lenders expect."
              />
              <PillarCard
                icon={ShieldCheck}
                title="Founder-First"
                desc="One-time fee. No subscriptions. No upsell calls. Yours forever."
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20 text-center">
          <div className="font-accent text-[11px] tracking-[0.3em] text-[#C5A059] mb-5">
            START YOUR EMPIRE
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            Ready to skip the six-month planning phase?
          </h2>
          <p className="mt-5 text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Pick your sector. Pay once. Open the PDF in under a minute and start building.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/#products"
              data-testid="about-cta-plans"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-bold text-sm tracking-wide px-7 py-3.5 transition"
            >
              SEE BUSINESS PLANS
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#bundle"
              data-testid="about-cta-bundle"
              className="inline-flex items-center justify-center gap-2 border border-[#C5A059]/50 hover:border-[#C5A059] text-[#C5A059] font-accent text-xs tracking-[0.2em] px-7 py-3.5 transition"
            >
              BUNDLE · SAVE 43%
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const StatCard = ({ label, value }) => (
  <div className="border-l-2 border-[#C5A059] pl-4">
    <div className="font-display text-3xl text-[#0A192F]">{value}</div>
    <div className="font-accent text-[10px] tracking-[0.25em] text-slate-500 mt-1">
      {label}
    </div>
  </div>
);

const PillarCard = ({ icon: Icon, title, desc }) => (
  <div className="bg-white border border-slate-200 p-5">
    <div className="flex h-10 w-10 items-center justify-center bg-[#0A192F] text-[#C5A059] mb-4">
      <Icon className="w-5 h-5" />
    </div>
    <div className="font-display text-lg text-[#0A192F] leading-tight">{title}</div>
    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);
